import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../../../shared/services/apiClient';
import toast from 'react-hot-toast';
import { Clock } from 'lucide-react';
import { isAdmin } from '../../../utils/permissions';
import { queryKeys } from '../../../shared/services/queryKeys';
import { getActivities } from '../../../shared/services/activityLogger';

type ActivityItem = {
  id: number | string;
  user: string;
  action: string;
  date: string; 
  module?: string;
  [k: string]: any;
};

type ActivityUser = {
  name?: string;
  companyId?: number | string;
  company?: string;
  companyName?: string;
};

type ActivityCompany = {
  id: number | string;
  name: string;
};

const ActivityView: React.FC<{ currentUser?: any }> = ({ currentUser }) => {
  const resolvedUser = useMemo(() => {
    if (currentUser) return currentUser;
    try { return JSON.parse(localStorage.getItem('currentUser') || 'null'); } catch { return null; }
  }, [currentUser]);

  // Access control - only admins can view activity
  if (!resolvedUser || !isAdmin(resolvedUser)) {
    return (
      <div className="p-8 bg-slate-900 min-h-[300px] rounded">
        <h2 className="text-2xl font-bold text-sky-400 mb-4">Acceso denegado</h2>
        <p className="text-slate-400">No tienes permisos para ver esta sección.</p>
      </div>
    );
  }

  const [activities, setActivities] = useState<ActivityItem[]>([]);

  // filters
  const [companyFilter, setCompanyFilter] = useState<number | ''>(resolvedUser.role === 'Controller de Empresa' ? (resolvedUser.companyId ?? '') : '');
  const [userFilter, setUserFilter] = useState<string>('');
  const [actionFilter, setActionFilter] = useState<string>('');

  const dashboardsQuery = useQuery({
    queryKey: queryKeys.dashboards,
    queryFn: async () => {
      const res = await api.get('/dashboards').catch(() => ({ data: [] }));
      return Array.isArray(res.data) ? res.data : (res.data?.dashboards || []);
    },
    enabled: Boolean(resolvedUser && isAdmin(resolvedUser)),
    staleTime: 120_000,
  });

  const datasourcesQuery = useQuery({
    queryKey: queryKeys.datasources,
    queryFn: async () => {
      const res = await api.get('/datasources').catch(() => ({ data: [] }));
      return Array.isArray(res.data) ? res.data : (res.data?.datasources || []);
    },
    enabled: Boolean(resolvedUser && isAdmin(resolvedUser)),
    staleTime: 120_000,
  });

  const usersQuery = useQuery<ActivityUser[]>({
    queryKey: queryKeys.users,
    queryFn: async () => {
      const res = await api.get('/users').catch(() => ({ data: [] }));
      return Array.isArray(res.data) ? res.data : (res.data?.users || []);
    },
    enabled: Boolean(resolvedUser && isAdmin(resolvedUser)),
    staleTime: 120_000,
  });

  const companiesQuery = useQuery<ActivityCompany[]>({
    queryKey: queryKeys.companies,
    queryFn: async () => {
      const res = await api.get('/companies').catch(() => ({ data: [] }));
      return Array.isArray(res.data) ? res.data : (res.data?.companies || []);
    },
    enabled: Boolean(resolvedUser && isAdmin(resolvedUser)),
    staleTime: 120_000,
  });

  const dashboards = dashboardsQuery.data ?? [];
  const datasources = datasourcesQuery.data ?? [];
  const users = usersQuery.data ?? [];
  const companies = companiesQuery.data ?? [];
  const loading = dashboardsQuery.isLoading || datasourcesQuery.isLoading || usersQuery.isLoading || companiesQuery.isLoading;

  // Helper: Obtener el nombre del usuario actual para actividades legacy
  const getCurrentUserName = React.useMemo(() => {
    return resolvedUser?.name || resolvedUser?.email || 'Usuario del sistema';
  }, [resolvedUser]);

  React.useEffect(() => {
    try {
      const acts: ActivityItem[] = [];
      
      // 1. Leer actividades registradas en localStorage (tiene delete, create, update)
      const loggedActivities = getActivities();
      loggedActivities.forEach((log: any) => {
        acts.push({
          id: log.id,
          user: log.user,
          action: log.action,
          date: log.date,
          module: log.module,
          metadata: log.metadata
        });
      });
      
      // 2. Agregar dashboards existentes (creaciones que pueden faltar en log)
      dashboards.forEach((d: any) => {
        // Solo agregar si no existe ya en el log
        const existsInLog = loggedActivities.some(
          (log: any) => log.metadata?.entityId === d.id && log.metadata?.type === 'create'
        );
        
        if (!existsInLog) {
          acts.push({
            id: `d-created-${d.id}`,
            user: d.createdBy || 'Usuario',
            action: 'Creó el dashboard',
            date: d.createdAt || new Date().toISOString(),
            module: 'Dashboards',
            metadata: { name: d.name, type: 'create', entityId: d.id }
          });
        }
        
        // Agregar actualizaciones si no están en el log
        if (d.updatedAt && d.updatedAt !== d.createdAt) {
          const updateExistsInLog = loggedActivities.some(
            (log: any) => 
              log.metadata?.entityId === d.id && 
              log.metadata?.type === 'update' &&
              new Date(log.date).getTime() === new Date(d.updatedAt).getTime()
          );
          
          if (!updateExistsInLog) {
            acts.push({
              id: `d-updated-${d.id}-${d.updatedAt}`,
              user: d.createdBy || 'Usuario',
              action: 'Actualizó el dashboard',
              date: d.updatedAt,
              module: 'Dashboards',
              metadata: { name: d.name, type: 'update', entityId: d.id }
            });
          }
        }
      });
      
      // 3. Agregar datasources existentes (creaciones que pueden faltar en log)
      datasources.forEach((ds: any) => {
        const existsInLog = loggedActivities.some(
          (log: any) => log.metadata?.entityId == ds.id && log.metadata?.type === 'create'
        );
        
        if (!existsInLog) {
          acts.push({
            id: `ds-created-${ds.id}`,
            user: getCurrentUserName,
            action: 'Creó una fuente de datos',
            date: ds.createdAt || new Date().toISOString(),
            module: 'Fuentes de Datos',
            metadata: { name: ds.name, type: 'create', entityId: ds.id }
          });
        }
        
        if (ds.updatedAt && ds.updatedAt !== ds.createdAt) {
          const updateExistsInLog = loggedActivities.some(
            (log: any) => 
              log.metadata?.entityId == ds.id && 
              log.metadata?.type === 'update' &&
              new Date(log.date).getTime() === new Date(ds.updatedAt).getTime()
          );
          
          if (!updateExistsInLog) {
            acts.push({
              id: `ds-updated-${ds.id}-${ds.updatedAt}`,
              user: getCurrentUserName,
              action: 'Actualizó una fuente de datos',
              date: ds.updatedAt,
              module: 'Fuentes de Datos',
              metadata: { name: ds.name, type: 'update', entityId: ds.id }
            });
          }
        }
      });

      // Ordenar por fecha descendente y limitar
      const sorted = acts.sort((a: any, b: any) => (b.date || '').localeCompare(a.date || ''));
      setActivities(sorted.slice(0, 200));
    } catch (err: any) {
      console.error('Error loading activity', err);
      if (err?.response?.status === 429) {
        toast.error('Demasiadas solicitudes. Por favor espera un momento.');
      } else {
        toast.error('Error al cargar actividad');
      }
    }
  }, [dashboards, datasources, getCurrentUserName]);

  const matchesAction = (item: ActivityItem) => {
    if (!actionFilter) return true;
    const type = item.metadata?.type || '';
    if (actionFilter === 'create') return type === 'create';
    if (actionFilter === 'update') return type === 'update';
    if (actionFilter === 'delete') return type === 'delete';
    return false;
  };

  // build filtered list: access control + filters
  const filtered = useMemo(() => {
    let list = activities.slice();
    // If Controller de Empresa, only show activities for users belonging to same company
    if (resolvedUser.role === 'Controller de Empresa') {
      const myCompanyId = Number(resolvedUser.companyId);
      // map users to companyId by name
      const namesOfCompany = users.filter(u => Number(u.companyId) === myCompanyId).map(u => (u.name || '').toLowerCase());
      list = list.filter(a => namesOfCompany.includes((a.user || '').toLowerCase()));
    }

    // Company filter (only for Controller General)
    if (resolvedUser.role === 'Controller General' && companyFilter) {
      const cid = Number(companyFilter);
      const names = users.filter(u => Number(u.companyId) === cid).map(u => (u.name || '').toLowerCase());
      list = list.filter(a => names.includes((a.user || '').toLowerCase()));
    }

    // user text filter
    if (userFilter && userFilter.trim()) {
      const q = userFilter.trim().toLowerCase();
      list = list.filter(a => (a.user || '').toLowerCase().includes(q));
    }

    // action type filter
    list = list.filter(matchesAction);

    return list.slice(0, 100);
  }, [activities, users, resolvedUser, companyFilter, userFilter, actionFilter]);
  return (
  <div className="flex flex-col h-full bg-[#080c14] text-slate-100 p-6">

    <div className="mb-8">
      <div className="text-xs uppercase tracking-widest text-sky-400 flex items-center gap-2 mb-2">
        <span className="w-6 h-px bg-sky-400" />
        Sistema
      </div>
      <h1 className="text-3xl font-semibold">
        Actividad del <span className="text-sky-400">Sistema</span>
      </h1>
      <p className="text-slate-400 text-base mt-2">
        Registro completo de acciones y eventos
      </p>
    </div>

    <div className="mb-8 flex flex-wrap items-center gap-4 bg-[#0e1420] border border-slate-800 rounded-xl px-6 py-5">

      <div className="flex-1 min-w-[240px] relative">
        <input
          value={userFilter}
          onChange={e => setUserFilter(e.target.value)}
          placeholder="Buscar usuario…"
          className="w-full bg-[#111923] border border-slate-700 rounded-lg pl-11 pr-4 py-2.5 text-sm text-slate-200 placeholder:text-slate-500 focus:outline-none focus:border-sky-400 transition-colors"
        />
        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500">
          <Clock size={16} />
        </span>
      </div>

      <select
        value={actionFilter}
        onChange={e => setActionFilter(e.target.value)}
        className="bg-[#111923] border border-slate-700 rounded-lg px-4 py-2.5 text-sm text-slate-300 focus:outline-none focus:border-sky-400 min-w-[160px]"
      >
        <option value="">Todas las acciones</option>
        <option value="create">Creación</option>
        <option value="update">Actualización</option>
        <option value="delete">Eliminación</option>
      </select>

      {resolvedUser.userType === 'INTERNAL' && resolvedUser.role === 'Controller General' && (
        <select
          value={companyFilter as any}
          onChange={e => setCompanyFilter(e.target.value ? Number(e.target.value) : '')}
          className="bg-[#111923] border border-slate-700 rounded-lg px-4 py-2.5 text-sm text-slate-300 focus:outline-none focus:border-sky-400 min-w-[180px]"
        >
          <option value="">Todas las empresas</option>
          {companies.map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
      )}

      <button
        onClick={() => { setUserFilter(''); setActionFilter(''); setCompanyFilter(''); }}
        className="text-sm text-sky-400 hover:bg-sky-400/10 px-4 py-2.5 rounded-lg border border-transparent hover:border-sky-400/30 transition-all"
      >
        Limpiar filtros
      </button>
    </div>

    <div className="flex-1 bg-[#0e1420] border border-slate-800 rounded-xl overflow-hidden">

      <div className="grid grid-cols-[180px_200px_1fr_200px_180px] px-6 py-4 border-b border-slate-800 text-xs uppercase tracking-wide text-slate-500 bg-[#080c14]/60 font-semibold">
        <span>Fecha</span>
        <span>Usuario</span>
        <span>Acción</span>
        <span>Empresa</span>
        <span>Módulo</span>
      </div>

      <div className="divide-y divide-slate-800">
        {loading && (
          <div className="p-12 text-slate-400 text-base text-center">Cargando actividad del sistema…</div>
        )}

        {!loading && filtered.length === 0 && (
          <div className="p-12 text-slate-400 text-base text-center">No se encontró actividad con los filtros aplicados</div>
        )}

        {!loading && filtered.map(a => {
          const companyName = (() => {
            const u = users.find(
              usr => (usr.name || '').toLowerCase() === (a.user || '').toLowerCase()
            );
            return u?.company || u?.companyName || '—';
          })();
          
          const formattedDate = (() => {
            try {
              const date = new Date(a.date);
              return date.toLocaleString('es-ES', { 
                day: '2-digit', 
                month: '2-digit', 
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              });
            } catch {
              return a.date;
            }
          })();

          return (
            <div
              key={a.id}
              className="grid grid-cols-[180px_200px_1fr_200px_180px] px-6 py-5 items-center hover:bg-sky-400/5 transition-colors"
            >
              <div className="text-sm text-slate-300">{formattedDate}</div>
              <div className="text-sm text-white font-medium">{a.user}</div>
              <div className="text-sm text-slate-300">{a.action}</div>
              <div className="text-sm text-slate-400">{companyName}</div>
              <div>
                <span className="inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-full border border-slate-700 text-sky-400 bg-sky-400/10">
                  <span className="w-1.5 h-1.5 rounded-full bg-sky-400" />
                  {a.module || 'Sistema'}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>

  </div>
);
};

export default ActivityView;
